"use client"

import { useState, useEffect, useMemo, useRef, useId } from "react"

import { cn } from "@/lib/utils"
import { analysisFunnelEnvelope, trackEvent } from "@/lib/analytics"
import { useVisibleImpression } from "@/hooks/use-visible-impression"
import { getRegionFromZipCode, REGIONAL_CLIMATE_DATA } from "@/data/regional-climate"
import { ANALYZING_FACTS, getAnalyzingFact } from "@/data/analyzing-facts"
import type { AnalysisMode as SharedAnalysisMode } from "@/lib/types/diagnosis"

type AnalysisMode = Exclude<SharedAnalysisMode, 'weed'>

/** Sells the multi-photo capability while the first analysis runs. Anonymous
 *  only, and always the first tip so it lands before attention drifts. */
const MULTI_PHOTO_TIP: Record<AnalysisMode, string> = {
  identify: "Free accounts read up to 3 photos for a sharper ID. Add one after this result.",
  diagnose: "Free accounts read up to 3 photos for a clearer diagnosis. Add one after this result.",
}

/** One delivered impression per attempt, module-scoped so a remount inside
 *  the same attempt cannot double-count. */
const multiPhotoTipKeys = new Set<string>()

/** How long the capability tip owns the "Did you know" slot before the
 *  generic fact takes over. The narration line below the heading rotates every
 *  2s; the tip must not ride that counter or it is gone before the visitor has
 *  read it. */
const TIP_LEAD_MS = 7000

// Lawnmower loader — a small mower pans left-to-right across a row of grass.
// The cut zone is a narrow band (CUT_W wide) that trails the mower. Grass
// behind the band has already regrown, so the regrow happens continuously
// as the mower passes — no pause, no separate regrow phase.
//
// Implementation: a <mask> on the tall-grass layer. White = visible,
// a moving black rect (the cut zone) carves out the band where stubble
// shows through. The band trails the mower's deck by `CUT_W` pixels.
//
// Cycle reset is invisible because both endpoints place the mower AND the
// cut zone fully offscreen — no flicker on loop.
const CYCLE = '3.0s'
const MOWER_OFFSCREEN_LEFT = -44
const MOWER_OFFSCREEN_RIGHT = 289
const CUT_W = 30
const CUT_OFFSCREEN_LEFT = MOWER_OFFSCREEN_LEFT - (CUT_W - 1)  // -73
const CUT_OFFSCREEN_RIGHT = MOWER_OFFSCREEN_RIGHT - (CUT_W - 1)  // 260
const VIEW_W = 260
const GROUND_Y = 80

function MowerLoader({ className }: { className?: string }) {
  const idPrefix = `mower-${useId().replace(/:/g, "")}`
  const brightBladeId = `${idPrefix}-blade-bright`
  const deepBladeId = `${idPrefix}-blade-deep`
  const cutMaskId = `${idPrefix}-cut-mask`

  // Pre-compute deterministic blade positions/heights so the layout matches
  // between the stubble and tall-grass layers (same x positions, same lean).
  // Small position jitter breaks up the regular grid for an organic lawn
  // feel without sacrificing the deterministic seed.
  const blades = Array.from({ length: 28 }).map((_, i) => {
    const jitter = ((i * 7) % 5) - 2  // -2, -1, 0, 1, 2 cyclically
    return {
      x: 8 + i * 8.4 + jitter * 0.6,
      h: 15 + ((i * 17) % 14),  // wider height range: 15–28
      lean: (i % 3) - 1,
      tone: i % 2 === 0 ? 'bright' : 'deep',
      stubbleTone: i % 3,  // 0/1/2 → three stubble colors
      stubbleH: 2.5 + ((i * 11) % 5) * 0.6,  // 2.5–4.9, more variation
    }
  })

  const STUBBLE_COLORS = ['#15803d', '#16a34a', '#22c55e'] as const

  return (
    <svg
      className={cn("ph-no-capture", className)}
      viewBox={`0 0 ${VIEW_W} 100`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={brightBladeId} x1="50%" y1="100%" x2="50%" y2="0%">
          <stop offset="0%" stopColor="#16a34a" />
          <stop offset="100%" stopColor="#4ade80" />
        </linearGradient>
        <linearGradient id={deepBladeId} x1="50%" y1="100%" x2="50%" y2="0%">
          <stop offset="0%" stopColor="#15803d" />
          <stop offset="100%" stopColor="#22c55e" />
        </linearGradient>
        {/* Cut-zone mask — white background = tall grass visible everywhere,
            moving black rect = trailing cut band where tall grass is hidden
            (revealing the stubble layer beneath). The black rect translates
            in lockstep with the mower, kept CUT_W pixels behind so the cut
            zone reads as the mower's wake. */}
        <mask id={cutMaskId} maskUnits="userSpaceOnUse" x="0" y="0" width={VIEW_W} height="100">
          <rect x="0" y="0" width={VIEW_W} height="100" fill="white" />
          <rect x="0" y="0" width={CUT_W} height="100" fill="black">
            <animateTransform
              attributeName="transform"
              type="translate"
              values={`${CUT_OFFSCREEN_LEFT} 0; ${CUT_OFFSCREEN_RIGHT} 0`}
              dur={CYCLE}
              repeatCount="indefinite"
            />
          </rect>
        </mask>
      </defs>

      {/* Ground — soft dotted soil line plus a few small pebbles/specks for
          texture, so the lawn doesn't sit on a bare canvas edge. */}
      <line
        x1="0"
        y1={GROUND_Y + 0.5}
        x2={VIEW_W}
        y2={GROUND_Y + 0.5}
        stroke="#86efac"
        strokeWidth="1"
        opacity="0.45"
        strokeDasharray="2 1.5"
      />
      <circle cx="32" cy={GROUND_Y + 1.6} r="0.7" fill="#a3a3a3" opacity="0.55" />
      <circle cx="79" cy={GROUND_Y + 1.2} r="0.5" fill="#a3a3a3" opacity="0.4" />
      <circle cx="148" cy={GROUND_Y + 1.8} r="0.8" fill="#a3a3a3" opacity="0.55" />
      <circle cx="201" cy={GROUND_Y + 1.3} r="0.5" fill="#a3a3a3" opacity="0.4" />
      <circle cx="237" cy={GROUND_Y + 1.6} r="0.7" fill="#a3a3a3" opacity="0.5" />

      {/* Stubble layer — always present, fully revealed once the mower has
          passed over each blade. Three-tone with opacity variation for a
          natural just-cut look. */}
      <g>
        {blades.map((b, i) => (
          <line
            key={`s${i}`}
            x1={b.x}
            y1={GROUND_Y}
            x2={b.x}
            y2={GROUND_Y - b.stubbleH}
            stroke={STUBBLE_COLORS[b.stubbleTone]}
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity={i % 4 === 0 ? 0.8 : i % 5 === 0 ? 0.9 : 1}
          />
        ))}
      </g>

      {/* Tall grass layer — masked so the moving cut zone hides a narrow
          band of grass directly behind the mower, revealing stubble beneath. */}
      <g mask={`url(#${cutMaskId})`}>
        {blades.map((b, i) => {
          const tip = GROUND_Y - b.h
          const leanX = b.x + b.lean * 1.4
          return (
            <path
              key={`t${i}`}
              d={`M${b.x - 0.7} ${GROUND_Y}
                  C ${b.x - 0.4} ${GROUND_Y - b.h * 0.55},
                    ${leanX - 0.4} ${GROUND_Y - b.h * 0.85},
                    ${leanX} ${tip}
                  C ${leanX + 0.4} ${GROUND_Y - b.h * 0.7},
                    ${b.x + 0.4} ${GROUND_Y - b.h * 0.4},
                    ${b.x + 0.7} ${GROUND_Y} Z`}
              fill={b.tone === 'bright' ? `url(#${brightBladeId})` : `url(#${deepBladeId})`}
            />
          )
        })}
      </g>

      {/* Mower — outer group handles horizontal travel (single linear sweep).
          The ground shadow sits inside the travel group but OUTSIDE the
          bounce group, so it stays planted on the ground while the body bobs
          above it. Everything else lives in the bounce group so it rises and
          falls together. */}
      <g>
        <animateTransform
          attributeName="transform"
          type="translate"
          values={`${MOWER_OFFSCREEN_LEFT} 0; ${MOWER_OFFSCREEN_RIGHT} 0`}
          dur={CYCLE}
          repeatCount="indefinite"
        />

        {/* Ground shadow — anchored to ground, doesn't bounce with the body.
            Slight asymmetry (longer right edge) suggests sun overhead-left. */}
        <ellipse cx="19" cy="82" rx="22" ry="1.8" fill="#000" opacity="0.16" />

        <g>
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0 0; 0 -0.8; 0 0.1; 0 -0.5; 0 0"
            keyTimes="0; 0.28; 0.55; 0.8; 1"
            dur="0.5s"
            repeatCount="indefinite"
          />

          {/* Grass clippings spraying out the rear of the deck. Staggered
              loops + slight vertical hop = continuous shower instead of a
              synchronized pulse. */}
          <g>
            <circle cx="3" cy="76" r="0.9" fill="#22c55e">
              <animate attributeName="cx" values="3; -9" dur="0.5s" repeatCount="indefinite" />
              <animate attributeName="cy" values="76; 71; 79" keyTimes="0; 0.4; 1" dur="0.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.95; 0.95; 0" keyTimes="0; 0.6; 1" dur="0.5s" repeatCount="indefinite" />
            </circle>
            <circle cx="3" cy="74" r="1.1" fill="#16a34a">
              <animate attributeName="cx" values="3; -11" dur="0.55s" begin="-0.18s" repeatCount="indefinite" />
              <animate attributeName="cy" values="74; 69; 78" keyTimes="0; 0.4; 1" dur="0.55s" begin="-0.18s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.95; 0.95; 0" keyTimes="0; 0.6; 1" dur="0.55s" begin="-0.18s" repeatCount="indefinite" />
            </circle>
            <circle cx="3" cy="78" r="0.8" fill="#15803d">
              <animate attributeName="cx" values="3; -8" dur="0.45s" begin="-0.32s" repeatCount="indefinite" />
              <animate attributeName="cy" values="78; 73; 80" keyTimes="0; 0.4; 1" dur="0.45s" begin="-0.32s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.95; 0.95; 0" keyTimes="0; 0.6; 1" dur="0.45s" begin="-0.32s" repeatCount="indefinite" />
            </circle>
          </g>

          {/* Cutting deck — low flat rectangle, wider than the engine */}
          <rect x="1" y="66" width="37" height="13" rx="1.8" fill="#374151" />
          {/* Deck top highlight (suggests 3D rim) */}
          <rect x="1" y="66" width="37" height="1.4" fill="#4b5563" />
          {/* Brand stripe + thin lighter underline */}
          <rect x="3" y="71" width="33" height="1.6" fill="#22c55e" opacity="0.9" />
          <rect x="3" y="73.6" width="33" height="0.5" fill="#86efac" opacity="0.55" />
          {/* Deck cooling vents */}
          <rect x="6" y="75.6" width="3" height="0.8" rx="0.3" fill="#111827" opacity="0.65" />
          <rect x="10.5" y="75.6" width="3" height="0.8" rx="0.3" fill="#111827" opacity="0.65" />
          <rect x="15" y="75.6" width="3" height="0.8" rx="0.3" fill="#111827" opacity="0.65" />
          {/* Leading-edge bumper — small chrome strip where the mower meets
              the uncut grass. Small detail that anchors the cutting end. */}
          <rect x="37.5" y="68" width="1.6" height="9" rx="0.4" fill="#9ca3af" />
          <rect x="37.5" y="68" width="1.6" height="2" fill="#d1d5db" />

          {/* Engine elements — wrapped in their own group so they vibrate
              independently of the body bounce. High-frequency micro-shake
              suggests a running motor without conflicting with the slower,
              terrain-bounce on the rest of the mower. */}
          <g>
            <animateTransform
              attributeName="transform"
              type="translate"
              values="0 0; 0.4 -0.3; -0.3 0.2; 0.3 0.4; -0.4 -0.2; 0 0"
              dur="0.07s"
              repeatCount="indefinite"
            />
            {/* Engine housing — sits on the leading half of the deck */}
            <rect x="19" y="52" width="15" height="14" rx="1.4" fill="#1f2937" />
            {/* Engine top cap (lighter so the silhouette reads as an L of stacked boxes) */}
            <rect x="19" y="52" width="15" height="1.4" fill="#374151" />
            {/* Air-filter housing */}
            <rect x="21" y="55.5" width="11" height="4" rx="0.8" fill="#4b5563" />
            {/* Brand decal */}
            <rect x="22.5" y="61" width="7" height="2.4" rx="0.4" fill="#22c55e" opacity="0.55" />
            {/* Muffler stub — where the exhaust puffs originate */}
            <rect x="17.5" y="58" width="2" height="2.6" rx="0.3" fill="#111827" />
            {/* Recoil starter — vertical pull-cord + horizontal T-grip */}
            <line x1="33" y1="48.5" x2="33" y2="52" stroke="#9ca3af" strokeWidth="0.6" />
            <line x1="30.8" y1="48.5" x2="35.2" y2="48.5" stroke="#1f2937" strokeWidth="1.4" strokeLinecap="round" />
          </g>

          {/* Muffler exhaust puffs — rise from the muffler stub on the back-left
              of the engine, drift up and slightly back as they expand and fade.
              Slim radii so the puffs read as wisps, not balloons. */}
          <circle cx="18.5" cy="58" r="1.0" fill="#9ca3af" opacity="0.5">
            <animate attributeName="opacity" values="0.5; 0" dur="0.9s" repeatCount="indefinite" />
            <animate attributeName="cy" values="58; 48" dur="0.9s" repeatCount="indefinite" />
            <animate attributeName="cx" values="18.5; 14" dur="0.9s" repeatCount="indefinite" />
            <animate attributeName="r" values="0.9; 2.0" dur="0.9s" repeatCount="indefinite" />
          </circle>
          <circle cx="18.5" cy="58" r="0.9" fill="#d1d5db" opacity="0.4">
            <animate attributeName="opacity" values="0.4; 0" dur="0.9s" begin="-0.45s" repeatCount="indefinite" />
            <animate attributeName="cy" values="58; 50" dur="0.9s" begin="-0.45s" repeatCount="indefinite" />
            <animate attributeName="cx" values="18.5; 15" dur="0.9s" begin="-0.45s" repeatCount="indefinite" />
            <animate attributeName="r" values="0.8; 1.7" dur="0.9s" begin="-0.45s" repeatCount="indefinite" />
          </circle>

          {/* Handle — two parallel rails, attaches to the rear edge of the
              deck, splays slightly outward as it rises so the silhouette
              reads as a real handlebar instead of a pole. */}
          <path d="M2 67 L -12 40" stroke="#1f2937" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M5.5 67 L -6 40" stroke="#1f2937" strokeWidth="1.8" strokeLinecap="round" />
          {/* Bolt caps where rails meet the crossbar — small dark dots that
              read as bolt heads, anchoring the joint visually. */}
          <circle cx="-12" cy="40" r="0.9" fill="#111827" />
          <circle cx="-6" cy="40" r="0.9" fill="#111827" />
          {/* Crossbar grip — sits across the top of both rails with foam wrap */}
          <line x1="-13" y1="39.6" x2="-5" y2="39.6" stroke="#111827" strokeWidth="2.4" strokeLinecap="round" />
          <line x1="-12" y1="39.2" x2="-6" y2="39.2" stroke="#4b5563" strokeWidth="1" strokeLinecap="round" />

          {/* Left (rear) wheel. Rotation rate matches the mower's screen
              velocity during the cutting phase (≈114 px/sec ÷ wheel
              circumference ≈ 0.28s/rev) so the wheels appear to roll, not
              spin idly. */}
          <g>
            <circle cx="8" cy="80" r="5" fill="#111827" />
            <circle cx="8" cy="80" r="2" fill="#374151" />
            <circle cx="8" cy="80" r="1" fill="#9ca3af" />
            {/* Spokes (cross hint at rotation) */}
            <line x1="8" y1="76" x2="8" y2="84" stroke="#9ca3af" strokeWidth="0.6" opacity="0.7" />
            <line x1="4" y1="80" x2="12" y2="80" stroke="#9ca3af" strokeWidth="0.6" opacity="0.7" />
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 8 80"
              to="360 8 80"
              dur="0.28s"
              repeatCount="indefinite"
            />
          </g>
          {/* Right (front) wheel — slightly larger; real push-mowers often
              run a larger leading wheel for over-the-curb clearance.
              Rotation slightly slower than the rear because larger
              circumference covers more ground per revolution. */}
          <g>
            <circle cx="31" cy="80" r="5.5" fill="#111827" />
            <circle cx="31" cy="80" r="2.2" fill="#374151" />
            <circle cx="31" cy="80" r="1.1" fill="#9ca3af" />
            <line x1="31" y1="75.5" x2="31" y2="84.5" stroke="#9ca3af" strokeWidth="0.6" opacity="0.7" />
            <line x1="26.5" y1="80" x2="35.5" y2="80" stroke="#9ca3af" strokeWidth="0.6" opacity="0.7" />
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 31 80"
              to="360 31 80"
              dur="0.31s"
              repeatCount="indefinite"
            />
          </g>
        </g>
      </g>
    </svg>
  )
}

interface AnalysisLoadingProps {
  zipCode?: string
  imageUrl?: string
  mode?: AnalysisMode
  funnelId?: string | null
  attemptId?: string | null
  authState?: 'anonymous' | 'authenticated' | 'unknown'
  className?: string
}

export function AnalysisLoading({
  zipCode,
  imageUrl,
  mode = 'identify',
  funnelId = null,
  attemptId = null,
  authState = 'unknown',
  className,
}: AnalysisLoadingProps) {
  const [messageIndex, setMessageIndex] = useState(0)
  // Stable per-mount fact pick so it doesn't shuffle on every message tick.
  const [factIndex] = useState(() => Math.floor(Math.random() * ANALYZING_FACTS.length))
  const rootRef = useRef<HTMLDivElement>(null)

  // Center the analyzing card in the viewport when it mounts. Analysis starts
  // right after the user taps Analyze mid-page, so on mobile the hero pins the
  // top of the viewport and this card renders half below the fold. One rAF so
  // the dynamic-import + AnimatePresence layout settles before measuring.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const raf = requestAnimationFrame(() => {
      el.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' })
    })
    return () => cancelAnimationFrame(raf)
  }, [])

  // Get region name from ZIP code for personalized message
  const regionName = useMemo(() => {
    if (!zipCode) return "your region"
    try {
      const region = getRegionFromZipCode(zipCode)
      return region && region !== 'Unknown' ? region : "your region"
    } catch {
      return "your region"
    }
  }, [zipCode])

  // The region's common grasses, so the narration can name real candidates
  // ("Comparing against Kentucky Bluegrass…") instead of staying generic.
  const regionGrasses = useMemo(() => {
    if (!zipCode) return null
    try {
      const region = getRegionFromZipCode(zipCode)
      const data = region && region !== 'Unknown' ? REGIONAL_CLIMATE_DATA[region] : undefined
      return data?.primaryGrasses && data.primaryGrasses.length >= 2 ? data.primaryGrasses : null
    } catch {
      return null
    }
  }, [zipCode])

  const identifyMessages = useMemo(() => [
    "Examining leaf blade width and texture...",
    "Checking growth habit patterns...",
    "Analyzing color and sheen characteristics...",
    "Looking for seed head structures...",
    regionGrasses
      ? `Comparing against ${regionGrasses[0]} and ${regionGrasses[1]}...`
      : "Comparing with cool-season varieties...",
    regionGrasses
      ? `Matching texture against grasses common in ${regionName}...`
      : "Comparing with warm-season varieties...",
    `Checking ${regionName} grass databases...`,
    "Analyzing root zone indicators...",
    "Evaluating drought tolerance markers...",
    "Cross-referencing with university databases...",
    "Calculating confidence scores...",
    "Preparing your personalized results...",
  ], [regionName, regionGrasses])

  const diagnoseMessages = useMemo(() => [
    "Examining for disease patterns...",
    "Checking for pest damage indicators...",
    "Identifying weed species...",
    "Analyzing discoloration patterns...",
    "Looking for fungal growth signs...",
    "Checking for insect damage marks...",
    `Cross-referencing ${regionName} common problems...`,
    "Evaluating nutrient deficiency indicators...",
    "Assessing environmental stress patterns...",
    "Matching symptoms to known conditions...",
    "Building treatment recommendations...",
    "Preparing your diagnosis report...",
  ], [regionName])

  const messages = mode === 'diagnose' ? diagnoseMessages : identifyMessages

  // The capability tip rides in front of the existing fact for an anonymous
  // visitor; a signed-in visitor keeps the single-fact behaviour unchanged.
  const isAnonymous = authState === 'anonymous'
  const tips = useMemo(() => {
    const fact = getAnalyzingFact(factIndex)
    return isAnonymous ? [MULTI_PHOTO_TIP[mode], fact] : [fact]
  }, [factIndex, isAnonymous, mode])
  const [tipIndex, setTipIndex] = useState(0)
  const tip = tips[Math.min(tipIndex, tips.length - 1)]

  useEffect(() => {
    if (tips.length < 2) return
    const timeout = setTimeout(() => setTipIndex(1), TIP_LEAD_MS)
    return () => clearTimeout(timeout)
  }, [tips.length])

  // attemptId is read off a ref upstream, so it can reach this loader a render
  // after it mounts. An impression emitted before the attempt id exists
  // carries attempt_id null and joins nothing, so hold it until the id lands.
  useVisibleImpression(
    multiPhotoTipKeys,
    isAnonymous && Boolean(attemptId),
    `${attemptId}:${mode}`,
    () => {
      trackEvent('multi_photo_tip_shown', {
        ...analysisFunnelEnvelope({
          funnelId,
          attemptId,
          mode,
          source: 'analysis_loading',
          authState,
        }),
        platform: 'web',
      })
    },
  )

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messages.length)
    }, 2000) // Slightly faster rotation with more messages

    return () => clearInterval(interval)
  }, [messages.length])

  return (
    <div ref={rootRef} className={cn("flex items-center justify-center min-h-[400px]", className)}>
      <div className="text-center space-y-6">
        {/* Primary visual: the user's own photo being "scanned" (recognition +
            labor perception). The shared view-transition-name lets it morph
            into the result hero. Falls back to the themed mower loader when no
            preview is available (e.g. a URL-resume path that skipped upload). */}
        {imageUrl ? (
          <div className="flex justify-center">
            <div
              className="relative w-44 h-44 md:w-52 md:h-52 rounded-2xl overflow-hidden shadow-lg bg-linear-to-br from-gray-100 to-gray-200"
              style={{ viewTransitionName: 'grass-photo' }}
            >
              <img
                src={imageUrl}
                alt="Your grass photo, being analyzed"
                className="w-full h-full object-cover"
              />
              {/* Scan sweep — a soft band travels top-to-bottom over the photo. */}
              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute inset-x-0 -top-1/3 h-1/3 bg-linear-to-b from-transparent via-green-400/50 to-transparent animate-scan-sweep" />
              </div>
              {/* Thin tracking line at the band's leading edge. */}
              <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-green-500/20 rounded-2xl" />
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <MowerLoader className="w-[280px] h-[108px]" />
          </div>
        )}

        {/* Rotating message - fixed height container prevents icon shift */}
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">
            {mode === 'diagnose' ? 'Diagnosing your lawn...' : 'Analyzing your lawn...'}
          </h2>
          <div className="h-[40px] flex items-center justify-center">
            <p
              key={messageIndex}
              className="text-sm text-gray-600 animate-in fade-in duration-500"
            >
              {messages[messageIndex]}
            </p>
          </div>
        </div>

        {/* Signature lawnmower — the loading indicator itself. Its continuous
            left-to-right mow communicates "work in progress" in place of a
            determinate bar. Only rendered when there's a photo up top; the
            no-photo fallback path already shows the mower as its primary
            visual (see above). */}
        {imageUrl && (
          <div className="flex justify-center">
            <MowerLoader className="w-[240px] h-[92px]" />
          </div>
        )}

        {/* Labor-perception beat: an expert aside + social proof, mirroring the
            mobile BuildingPlanOverlay. Fixed min-height to avoid layout shift. */}
        <div className="mx-auto max-w-sm space-y-2">
          <div className="flex items-start gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-left min-h-[52px]">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-green-700 mt-0.5 whitespace-nowrap">
              Did you know
            </span>
            <span className="text-[12.5px] text-gray-600 leading-snug">
              {tip}
            </span>
          </div>
          <p className="text-[11.5px] text-gray-400">
            Analyzing your lawn photo
          </p>
        </div>
      </div>
    </div>
  )
}
